'use client';
import dynamic from 'next/dynamic';
import React, { useEffect, useMemo, useState } from 'react';
import Empty from '@/components/atoms/empty';
import Skeleton from '@/components/atoms/skeletons';
import Collapse from '@/components/atoms/collapse';
import IndicatorCollapseItems from './indicator-collapse-item';
import ChartPackageSkeleton from '@/components/molecules/skeleton-component/chartPacakge';
import useGetTags from '@/helpers/useGetTags';
import { SCREEN } from '@/statics/constants';
import { useGetTagChartData } from '@/helpers/useGetTagChartData';
import { ContextualIndicator, HomeTags } from '@/interfaces/home';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import Icon from '@/components/atoms/icon';
import dayjs from 'dayjs';
import { slugify } from '@/utils/funtion';

const MapsPackage = dynamic(
  () => import('@/components/templates/mapsPackage'),
  {
    ssr: false,
    loading: () => {
      return <ChartPackageSkeleton />;
    },
  },
);

const ChartPackage = dynamic(
  () => import('@/components/templates/chartPackage'),
  {
    ssr: false,
    loading: () => {
      return <ChartPackageSkeleton />;
    },
  },
);

function HomeLocalEconomics({ homepage }: { homepage: boolean }) {
  const { chartData, handleGetChardData, loading, handleGetChartDataByRange } =
    useGetTagChartData();
  const { tags, isLoading } = useGetTags('du-lieu-dia-phuong', SCREEN.HOME);
  const [selectedTime, setSelectedTime] = useState<string | undefined>(
    undefined,
  );
  const [type, setType] = useState('');
  const [initialData, setInitialData] = useState({
    key: '',
    value: undefined as any,
  });
  const area = 'VN';
  const [activeItem, setActiveItem] = useState({
    groupKey: '0',
    itemId: '',
  });

  useEffect(() => {
    try {
      if (tags?.children?.length && !activeItem.itemId) {
        const validGroup = tags.children.find(
          (group: HomeTags) => group.contextualIndicators?.length > 0,
        );

        if (validGroup) {
          const groupKey = tags.children.indexOf(validGroup).toString();
          const sortedIndicators = handleSortedIndicators(
            validGroup.contextualIndicators,
          );

          if (sortedIndicators.length > 0) {
            const firstIndicator = sortedIndicators[0];
            const { indicator, industryIndicator, localIndicator } =
              firstIndicator;
            const selectedIndicator =
              indicator || industryIndicator || localIndicator;

            if (selectedIndicator) {
              setActiveItem({
                groupKey,
                itemId: selectedIndicator.id,
              });
              handleGetChardData(
                selectedIndicator.id,
                selectedIndicator.type,
                selectedIndicator.isStructure ? 1 : 0,
                '',
                selectedIndicator.isObjectOneTime ? 1 : 0,
              );
            }
          }
        }
      }
    } catch (error) {
      console.log(' 🚀- DaiNQ - 🚀: -> useEffect -> error:', error);
    }
  }, [tags?.children, activeItem.itemId]);

  useEffect(() => {
    const values = chartData?.values || {};
    for (const key in values) {
      if (Object.keys(values[key]).length > 0) {
        setInitialData({
          key,
          value: values[key],
        });
      }
    }
  }, [chartData]);

  useEffect(() => {
    if (selectedTime) {
      handleGetChardData(
        activeItem.itemId,
        'local_indicator',
        chartData?.isStructure ? 1 : 0,
        selectedTime,
        chartData?.isObjectOneTime ? 1 : 0,
      );
    }
  }, [selectedTime]);

  const handleSelectTime = (value: string) => {
    setSelectedTime(value);
  };

  const mappingData: Record<string, any> = {
    indicator: chartData?.values?.indicator,
    industryIndicator: chartData?.values?.industryIndicator,
    localIndicator: chartData?.values?.localIndicator,
  };

  const handleSortedIndicators = (
    contextualIndicators: ContextualIndicator[],
  ) => {
    return [...contextualIndicators].sort((a, b) => {
      const aUpdatedAt = dayjs(
        a.localIndicator?.updatedAt ||
          a.indicator?.updatedAt ||
          a.industryIndicator?.updatedAt,
      );
      const bUpdatedAt = dayjs(
        b.localIndicator?.updatedAt ||
          b.indicator?.updatedAt ||
          b.industryIndicator?.updatedAt,
      );
      const threeDaysAgo = dayjs().subtract(3, 'day');

      const isARecent = aUpdatedAt.isAfter(threeDaysAgo);
      const isBRecent = bUpdatedAt.isAfter(threeDaysAgo);

      if (isARecent && !isBRecent) return -1;
      if (!isARecent && isBRecent) return 1;

      return bUpdatedAt.valueOf() - aUpdatedAt.valueOf();
    });
  };

  const items =
    tags?.children &&
    tags?.children.map((item: HomeTags, index: number) => {
      const sortedIndicators = handleSortedIndicators(
        item?.contextualIndicators,
      );
      const hasToday = sortedIndicators.some((i) => {
        const updatedAt = dayjs(
          i.localIndicator?.updatedAt ||
            i.indicator?.updatedAt ||
            i.industryIndicator?.updatedAt,
        );
        return updatedAt.isAfter(dayjs().subtract(3, 'day'));
      });

      return {
        key: index.toString(),
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {item?.title}
            {hasToday && (
              <div className="collapse-header_new">
                <Icon iconName="newest" size="28x28" />
              </div>
            )}
          </div>
        ),
        className: hasToday ? 'has-today' : '',
        children: (
          <>
            {!sortedIndicators.length ? (
              <Empty />
            ) : (
              sortedIndicators.map((i: ContextualIndicator) => {
                const localIndicator = i.localIndicator;
                const isToday = dayjs(
                  (i.indicator || i.industryIndicator || i.localIndicator)
                    .updatedAt,
                ).isAfter(dayjs().subtract(3, 'day'));

                return (
                  <IndicatorCollapseItems
                    key={localIndicator?.id}
                    id={localIndicator?.id}
                    slug={localIndicator?.slug}
                    type={localIndicator?.type}
                    activeItemId={activeItem?.itemId}
                    name={localIndicator?.name}
                    isStructure={localIndicator?.isStructure}
                    onItemClick={() => {
                      setActiveItem({
                        groupKey: index.toString(),
                        itemId: localIndicator?.id,
                      });
                      setType(localIndicator?.type);
                      handleGetChardData(
                        localIndicator?.id,
                        localIndicator?.type,
                        localIndicator?.isStructure ? 1 : 0,
                        '',
                        localIndicator?.isObjectOneTime ? 1 : 0,
                      );
                    }}
                    hasMap
                    isObjectOneTime={false}
                    isToday={isToday}
                  />
                );
              })
            )}
          </>
        ),
      };
    });

  const memoryChart = useMemo(() => {
    if ((!chartData && isLoading) || (!chartData && loading))
      return <ChartPackageSkeleton />;

    if (!chartData)
      return (
        <div className="indicator-session-chart_empty">
          <Empty />
        </div>
      );

    if (
      (initialData.key === 'localIndicator' &&
        !!chartData?.values?.localIndicator?.map?.length) ||
      !!initialData?.value?.map?.length
    ) {
      return (
        <MapsPackage
          area={area}
          id={chartData?.id}
          dataMaps={chartData?.values?.localIndicator?.map || []}
          original={chartData?.original}
          originalUrl={chartData?.originalUrl}
          loading={chartData?.loading}
          unit={chartData?.unit}
          mapTitle={chartData?.name}
          timeData={chartData?.yearList}
          chartType={chartData?.chartType}
          onSelectTime={handleSelectTime}
        />
      );
    }

    const data =
      !chartData?.isStructure &&
      !chartData?.isTwoUnit &&
      !chartData?.isObjectOneTime
        ? mappingData[initialData.key]?.often
          ? mappingData[initialData.key]?.often
          : mappingData[initialData.key]?.chart
        : mappingData[initialData.key]?.children?.length
          ? mappingData[initialData.key]?.children
          : mappingData[initialData.key]?.children?.response
            ? mappingData[initialData.key]?.children?.response
            : mappingData[initialData.key]?.chart;

    return (
      <ChartPackage
        data={data}
        isHomePage={homepage}
        isStructure={chartData?.isStructure}
        name={chartData?.name}
        slug={slugify(chartData?.name)}
        id={chartData?.id}
        original={chartData?.original}
        originalUrl={chartData?.originalUrl}
        unit={chartData?.unit}
        loading={loading}
        dataType={chartData?.dataType}
        chartType={chartData?.chartType || 'column'}
        isTwoUnit={chartData?.isTwoUnit}
        isObjectOneTime={chartData?.isObjectOneTime}
        handleClickRangeTime={(item) => {
          handleGetChartDataByRange(item, type, chartData?.isStructure ? 1 : 0);
        }}
        chartHeight={620}
        typeData={type}
        parentId={chartData?.parentId}
      />
    );
  }, [chartData, mappingData, initialData, area, type]);

  return (
    <div className="t-home-economics">
      <div className="t-home-economics_left local-economics">
        {isLoading ? (
          <Skeleton
            type="node"
            style={{ width: '100%', minHeight: 400 }}
            active
          />
        ) : (
          <React.Fragment>
            {!tags?.children?.length ? (
              <Empty />
            ) : (
              <Collapse items={items} accordion={true} />
            )}
          </React.Fragment>
        )}
      </div>
      <div className="t-home-economics_right local-economics">
        <div className="local-economics-data">{memoryChart}</div>
      </div>
    </div>
  );
}

export default React.memo(HomeLocalEconomics);
